"use client";

import { Button, Checkbox, Divider, Input } from "@heroui/react";
import { addToast } from "@heroui/toast";
import { Icon } from "@iconify/react";
import { getAuth } from "firebase/auth";
import { validatePassword } from "firebase/auth/web-extension";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React from "react";

import { Link, useRouter } from "@/i18n/routing";
import { signInWithGoogle, signUpWithEmail } from "@/lib/firebase-auth";

export default function RegisterForm() {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/";

  const toggleVisibility = () => setIsVisible(!isVisible);
  const toggleConfirmVisibility = () => setIsConfirmVisible(!isConfirmVisible);

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    try {
      const { user } = await signInWithGoogle();

      if (user) {
        router.push(redirectTo);
      } else {
        setIsLoading(false);
      }
    } catch {
      addToast({
        title: t("auth.signup.errors.googleSignup"),
        color: "danger",
        severity: "danger",
      });
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    // Validate password strength with Firebase
    const auth = getAuth();
    const passwordValidation = await validatePassword(auth, password);

    if (password !== confirmPassword) {
      addToast({
        title: t("auth.signup.errors.passwordMismatch"),
        color: "danger",
        severity: "danger",
      });

      return;
    }

    if (!passwordValidation.isValid) {
      if (!passwordValidation.meetsMinPasswordLength) {
        addToast({
          title: t("auth.signup.errors.passwordTooShort"),
          color: "danger",
          severity: "danger",
        });

        return;
      }
      if (!passwordValidation.meetsMaxPasswordLength) {
        addToast({
          title: t("auth.signup.errors.passwordTooLong"),
          color: "danger",
          severity: "danger",
        });

        return;
      }
      addToast({
        title: t("auth.signup.errors.passwordInvalid"),
        color: "danger",
        severity: "danger",
      });

      return;
    }

    const { error } = await signUpWithEmail(email, password, name);

    if (error) {
      let errorMessage = t("auth.signup.errors.signupFailed");

      if (error.includes("auth/email-already-in-use")) {
        errorMessage = t("auth.signup.errors.emailInUse");
      } else if (error.includes("auth/invalid-email")) {
        errorMessage = t("auth.signup.errors.invalidEmail");
      } else if (error.includes("auth/weak-password")) {
        errorMessage = t("auth.signup.errors.weakPassword");
      }

      addToast({
        title: errorMessage,
        severity: "danger",
      });
    } else {
      addToast({
        title: t("auth.signup.success.accountCreated"),
        color: "success",
        severity: "success",
      });
      router.push(redirectTo);
    }
  };

  return (
    <div className="mx-auto w-full max-w-sm">
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <Input
          isRequired
          label={t("auth.signup.name")}
          name="name"
          placeholder={t("auth.signup.namePlaceholder")}
          type="text"
          variant="bordered"
          autoComplete="name"
        />
        <Input
          isRequired
          label={t("auth.signup.email")}
          name="email"
          placeholder={t("auth.signup.emailPlaceholder")}
          type="email"
          variant="bordered"
          autoComplete="email"
        />
        <Input
          isRequired
          endContent={
            <button type="button" onClick={toggleVisibility}>
              {isVisible ? (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-closed-linear"
                />
              ) : (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-bold"
                />
              )}
            </button>
          }
          label={t("auth.signup.password")}
          name="password"
          placeholder={t("auth.signup.passwordPlaceholder")}
          type={isVisible ? "text" : "password"}
          variant="bordered"
          autoComplete="new-password"
        />
        <Input
          isRequired
          endContent={
            <button type="button" onClick={toggleConfirmVisibility}>
              {isConfirmVisible ? (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-closed-linear"
                />
              ) : (
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon="solar:eye-bold"
                />
              )}
            </button>
          }
          label={t("auth.signup.confirmPassword")}
          name="confirmPassword"
          placeholder={t("auth.signup.confirmPasswordPlaceholder")}
          type={isConfirmVisible ? "text" : "password"}
          variant="bordered"
          autoComplete="new-password"
        />

        <div className="mt-2 flex items-start gap-2">
          <Checkbox isRequired size="lg" />
          <span className="text-small">
            {t("auth.signup.agreeWith")}&nbsp;
            <Link
              className="text-small text-primary hover:underline"
              href="/privacy"
            >
              {t("auth.signup.privacy")}
            </Link>
          </span>
        </div>

        <Button color="primary" size="lg" type="submit">
          {t("auth.signup.signUp")}
        </Button>
      </form>

      <div className="flex items-center gap-4 py-4">
        <Divider className="flex-1" />
        <p className="shrink-0 text-tiny text-default-500">
          {t("auth.signup.or")}
        </p>
        <Divider className="flex-1" />
      </div>

      <div className="flex flex-col gap-2">
        <Button
          isLoading={isLoading}
          size="lg"
          startContent={<Icon icon="flat-color-icons:google" width={24} />}
          variant="bordered"
          onPress={handleGoogleSignup}
        >
          {t("auth.signup.continueWithGoogle")}
        </Button>
      </div>

      <p className="mt-6 text-center text-small">
        {t("auth.signup.haveAccount")}&nbsp;
        <Link className="text-sm text-primary hover:underline" href="/login">
          {t("auth.signup.logIn")}
        </Link>
      </p>
    </div>
  );
}
